import React from 'react';

import { GetAdyenPayments_operativeViewer_balanceLegalEntity_balanceAccounts_transactions } from '../__queries__';
import { useCurrency, useI18n } from '~Hooks';
import styled from '@emotion/styled';
import { StyledTable } from '~Styles/table';
import { CircleSpinner } from 'react-spinners-kit';

const getTypeStyles = (type: any) => {
  switch (type) {
    case 'purchase':
      return { bg: '#d1fae5', color: '#047857' };
    case 'refusal':
      return { bg: '#fee2e2', color: '#b91c1c' };
    case 'payout':
      return { bg: '#bfdbfe', color: '#1d4ed8' };
    case 'refund':
      return { bg: '#ffedd5', color: '#d97706' };
    case 'online':
      return { bg: '#000', color: '#fff' };
    default:
      return { bg: '#f3f4f6', color: '#4b5563' };
  }
};

const StyledTransactionType = styled.div<{ bg: string; color: string }>`
  background-color: ${({ bg }) => bg};
  color: ${({ color }) => color};
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline;
`;

const LoaderWrapper = styled.div`
  justify-self: center;
`;

const StyledCardIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  .icon {
    font-size: 1.5rem;
  }

  .label {
    font-size: 0.75rem;
    font-weight: 600;
  }
`;

const getCardIcon = (identifier?: string) => {
  if (!identifier) return null;

  switch (identifier.toLowerCase()) {
    case 'visa':
      return (
        <span className="label" style={{ color: '#1337cb' }}>
          VISA
        </span>
      );
    case 'mc':
      return (
        <span className="label" style={{ color: '#ff5f00' }}>
          MC
        </span>
      );
    case 'amex':
      return (
        <span className="label" style={{ color: '#006fcf' }}>
          AMEX
        </span>
      );
    case 'swish':
      return (
        <span className="label" style={{ color: '#ec4899' }}>
          Swish
        </span>
      );
    case 'vipps':
      return (
        <span className="label" style={{ color: '#ff5b24' }}>
          Vipps
        </span>
      );
    default:
      return <div style={{ color: '#4b5563', fontSize: '1.5rem' }} />;
  }
};

const TransactionTypeDisplay = ({ type }: { type: any }) => {
  const { bg, color } = getTypeStyles(type);
  const { i18n } = useI18n();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <StyledTransactionType bg={bg} color={color}>
        {type ? i18n('Transactions.' + type) : i18n('Transactions.unknown')}
      </StyledTransactionType>
    </div>
  );
};

export interface TransactionTableProps {
  transactions: GetAdyenPayments_operativeViewer_balanceLegalEntity_balanceAccounts_transactions[];
}

export function TransactionTable({ transactions }: TransactionTableProps) {
  const { i18n } = useI18n();
  const currency = useCurrency();

  return (
    <StyledTable>
      <table>
        <thead>
          <tr>
            <th>{i18n('Transactions.CreatedAt')}</th>
            <th>{i18n('Transactions.Ref')}</th>
            <th>{i18n('Transactions.Card')}</th>
            <th>{i18n('Transactions.Type')}</th>
            <th className="text-right">{i18n('Transactions.Amount')}</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((transaction) => (
            <tr key={transaction.id}>
              <td>
                <span>{new Date(transaction.created_at).toLocaleDateString()}</span>
                <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                  {new Date(transaction.created_at).toLocaleTimeString()}
                </span>
              </td>
              <td>{transaction.pspReference}</td>
              <td>
                <StyledCardIconContainer>
                  {getCardIcon(transaction.paymentMethod)}
                  {transaction.methodIdentifier && (
                    <span style={{ color: '#6b7280' }}>{transaction.methodIdentifier}</span>
                  )}
                </StyledCardIconContainer>
              </td>
              <td>
                <TransactionTypeDisplay type={transaction.type} />
              </td>
              <td className={`amount ${transaction.amount < 0 ? 'negative' : 'positive'}`}>
                {currency.format(transaction.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </StyledTable>
  );
}

export default React.memo(TransactionTable);
